/**
 * דף הקטלוג — רשת מוצרים שטוחה + אינסוף גלילה — Feedback1.md סעיף 8 (20.08.2026).
 *
 * ⭐ **שינוי מבנה מלא לבקשת טל:** "יראו את המוצרים עצמם ולא קטגוריות (בכלל
 *    לא יהיה ניווט לפי קטגוריות)... הכי חשוב גם הווריאנטים... בנפרד בדף
 *    הקטלוג." ⛔ **מחליף לגמרי** את רשת הקטגוריות + `CategoryPage.tsx` הישנים
 *    (סעיפים 8.4, מ-19.08.2026).
 *
 * ⭐ **אינסוף גלילה (8.7א):** טעינה ראשונה מציגה `PAGE_SIZE` כרטיסים; גלילה
 *    לתחתית טוענת את העמוד הבא דרך `IntersectionObserver` על "sentinel" —
 *    ⛔ לא כפתורי "עמוד הבא". חיפוש מאפס את הגלילה מ-0 (8.7א).
 */
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { MagnifyingGlass, Package } from '@phosphor-icons/react';
import { isPublicApiError, listCatalogCards, type CatalogCard } from '../publicApi/catalogApi.js';
import { PHONE_WHATSAPP_HREF } from '../content/businessInfo.js';
import { LinkButton } from '../components/Button.js';
import { ProductCard } from '../components/ProductCard.js';
import { EmptyState } from '../components/EmptyState.js';
import { SkeletonCardGrid } from '../components/Skeleton.js';
import { TintedSection } from '../components/TintedSection.js';

const PAGE_SIZE = 12;

export function CatalogPage() {
  const [cards, setCards] = useState<CatalogCard[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qInput, setQInput] = useState('');
  const [activeQ, setActiveQ] = useState<string | undefined>(undefined);
  const [announcement, setAnnouncement] = useState('');
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  // ⭐ באג אמיתי, 20.08.2026: `IntersectionObserver` נבנה מחדש בכל שינוי
  //    ב-`cards.length` (למטה) — כשה-sentinel עדיין בתוך ה-`rootMargin`
  //    ברגע שהמופע הישן מתנתק והחדש מתחבר, שני המופעים עלולים לירות
  //    "intersecting" עבור אותה נקודת גלילה בפועל, לפני שה-state
  //    `loadingMore` (אסינכרוני) הספיק להתעדכן ולחסום את הקריאה השנייה.
  //    התוצאה: אותו `offset` נשלף פעמיים, אותם כרטיסים נוספים פעמיים עם
  //    אותו `cardKey` — React לא סובל מפתחות כפולים ומשמיט/מכפיל כרטיסים
  //    בצורה לא צפויה בגלילה. `fetchingRef` (רגיל, מתעדכן באופן סינכרוני,
  //    ⛔ לא state) חוסם קריאות חופפות באמת; הסינון לפי `cardKey` בהמשך
  //    הוא הגנה כפולה למקרה שכפילות עוד מגיעה מהרשת בכל זאת.
  const fetchingRef = useRef(false);

  const fetchPage = useCallback(async (offset: number, q: string | undefined, replace: boolean) => {
    const result = await listCatalogCards({ limit: PAGE_SIZE, offset, q });
    setCards((prev) => {
      if (replace) return result.cards;
      const existingKeys = new Set(prev.map((card) => card.cardKey));
      const newCards = result.cards.filter((card) => !existingKeys.has(card.cardKey));
      return [...prev, ...newCards];
    });
    setHasMore(result.hasMore);
    return result.cards.length;
  }, []);

  // ⭐ טעינה ראשונית + כל שינוי חיפוש (activeQ) — מתחיל תמיד מ-offset 0.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPage(0, activeQ, true)
      .catch((err) => {
        if (!cancelled) setError(isPublicApiError(err) ? err.message : 'שגיאה בטעינת הקטלוג.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeQ, fetchPage]);

  // ⭐ IntersectionObserver על ה-sentinel — טוען את העמוד הבא כשהוא נכנס
  // ל-viewport. נבנה מחדש בכל שינוי ב-`cards.length` כדי שה-offset הבא
  // (`cards.length` הנוכחי) יהיה תמיד עדכני בסגירה (closure).
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || loading || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;
        // ⭐ `fetchingRef` (סינכרוני) הוא החוסם האמיתי — נבדק ונקבע באותה
        //    "נשימה" לפני שקוראים ל-state async. `loadingMore` (state) נשאר
        //    רק לתצוגה ("טוען עוד מוצרים…"), ⛔ לא כמנגנון החסימה עצמו.
        if (fetchingRef.current) return;
        fetchingRef.current = true;
        setLoadingMore(true);
        fetchPage(cards.length, activeQ, false)
          .then((count) => setAnnouncement(`נטענו עוד ${count} מוצרים.`))
          .catch((err) => {
            setError(isPublicApiError(err) ? err.message : 'שגיאה בטעינת מוצרים נוספים.');
          })
          .finally(() => {
            fetchingRef.current = false;
            setLoadingMore(false);
          });
      },
      { rootMargin: '400px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loading, hasMore, cards.length, activeQ, fetchPage]);

  function handleSearchSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setActiveQ(qInput.trim() || undefined);
  }

  return (
    // ⭐ 16.08.2026 - `TintedSection` (כיוון עיצוב "ג'") משותף לכל הדפים
    // הראשיים בניווט, לבקשת אריאנה, כדי שהזהות הוויזואלית תישאר זהה בכולם.
    <TintedSection>
      <div className="mx-auto max-w-6xl px-6 py-16">
        <h1 className="text-3xl font-bold">הקטלוג שלנו</h1>
        <p className="text-muted mt-2 max-w-2xl">
          כל המוצרים שלנו במקום אחד. הוסיפו לעגלת הבקשות כדי לקבל הצעת מחיר.
        </p>

        <form
          role="search"
          onSubmit={handleSearchSubmit}
          className="mt-6 flex max-w-md items-end gap-2"
        >
          <div className="flex-1">
            <label htmlFor="catalog-search" className="block text-sm font-semibold">
              חיפוש מוצר
            </label>
            <input
              id="catalog-search"
              type="search"
              value={qInput}
              onChange={(event) => setQInput(event.target.value)}
              className="bg-canvas mt-1 w-full rounded-md border border-line px-3 py-2"
              placeholder="לדוגמה: שטוצר, תריס…"
            />
          </div>
          {/* ⭐ נוסף — פידבק אריאנה: על רקע `TintedSection` הקלוש, שדה+כפתור
              שקופים כמעט נעלמים. רקע לבן (כמו כרטיסי המוצר) כדי שיהיה ברור
              ללקוח שיש כאן שדה חיפוש. */}
          <button type="submit" className="btn btn-secondary bg-canvas">
            חיפוש
          </button>
        </form>

        {error && (
          <p role="alert" className="text-danger mt-6">
            {error}
          </p>
        )}

        {loading && (
          <>
            <p className="sr-only" aria-live="polite">
              טוען מוצרים…
            </p>
            <SkeletonCardGrid
              count={8}
              className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            />
          </>
        )}

        {!loading && !error && cards.length === 0 && (
          <EmptyState
            icon={activeQ ? MagnifyingGlass : Package}
            message="לא נמצאו מוצרים תואמים."
          />
        )}

        {!loading && cards.length > 0 && (
          // ⭐ DESIGN.md 5.3 — "1 עמודה מובייל · 2 טאבלט · 3 דסקטופ (עד 4 ב-xl)".
          <ul className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {cards.map((card, index) => (
              <ProductCard key={card.cardKey} card={card} index={index} />
            ))}
          </ul>
        )}

        {/* ⭐ אזור sentinel — לא נראה ולא מפעיל שום דבר בעצמו, רק מפעיל את
            הטעינה הבאה כשהוא נכנס ל-viewport. */}
        <div ref={sentinelRef} aria-hidden="true" className="h-1" />

        {loadingMore && (
          <p className="text-muted mt-4 text-center text-sm" aria-hidden="true">
            טוען עוד מוצרים…
          </p>
        )}
        {/* ⭐ 8.7א — הכרזה לקוראי מסך על טעינה נוספת (אזור נפרד מה-skeleton). */}
        <p role="status" aria-live="polite" className="sr-only">
          {announcement}
        </p>

        {/* ⭐ סוגר את הפער בין מלוא תחום העיסוק לקטלוג המצומצם (PRD 14.2) */}
        <div className="mt-16 rounded-lg border border-line bg-surface p-6 text-center">
          <h2 className="text-lg font-bold">לא מצאתם את מה שחיפשתם?</h2>
          <p className="text-muted mt-2">
            אנחנו מייצרים גם לפי שרטוט והזמנה אישית - שלחו לנו את הפרטים בוואטסאפ.
          </p>
          <LinkButton href={PHONE_WHATSAPP_HREF} newTab className="mt-4 inline-flex">
            שליחת שרטוט בוואטסאפ
          </LinkButton>
        </div>
      </div>
    </TintedSection>
  );
}
